/* PARACELART — live embroidery preview (22 Sep 2026, photo linen + book format 24 Sep 2026)
   Draws the shopper's linen cover with their names, date, lettering style
   and thread colour, so they can see their words before they are stitched.
   - The cover is a real photograph of the blank linen book, one per format
     (assets/paracelart-linen-blank.jpg vertical, paracelart-linen-blank-h.jpg
     horizontal; greyscale) re-dyed in linear light to
     the chosen linen hex, so the true weave, slubs, spine and shading show in
     every colour. If the photo cannot load, a drawn linen texture is used.
   - Book format: the shopper picks Vertical or Horizontal (same book, turned);
     the cover, the linen weave and the motif artwork follow the choice. Horizontal
     uses the product's horizontal motif artwork when one is set.
   - Product page: reads the personalization form and redraws on every change,
     pre-fills the form from URL parameters (shareable / proof links) and keeps
     the hidden "_Proof link" property up to date for the studio.
   - Cart page: redraws each personalized line from its saved properties.
   - Per-design layouts (26 Sep 2026): a product whose metafield custom.preview_layout holds a
     layout (see paintLayout) is drawn as its own design - fixed phrases, names joined by a
     heart, initials inside the motif, "EST." year lines, single-colour artwork dyed to the
     chosen thread or multi-colour artwork kept as stitched - instead of the generic
     motif / names / date stack.
   No dependencies. Fonts come from the theme's @font-face rules. */
(() => {
  'use strict';
  const SHAPES = { portrait: [900, 1080], landscape: [1080, 788], square: [960, 960] };
  const DEFAULT_FONT = 'Cormorant Garamond';
  const DATE_STYLE = { family: DEFAULT_FONT, weight: 500, spacing: 0.18, transform: 'uppercase' };
  const ASSET_BASE = (() => {
    try {
      const src = document.currentScript && document.currentScript.src;
      return src ? src.replace(/paracelart-preview\.js.*$/, '') : '';
    } catch (e) { return ''; }
  })();
  // real blank-book photos; spine = share of the photo's width taken by the spine
  const PHOTOS = {
    portrait: { file: 'paracelart-linen-blank.jpg', spine: 0.043 },
    landscape: { file: 'paracelart-linen-blank-h.jpg', spine: 0.028 }
  };
  const textureCache = new Map();
  const photoCache = new Map();
  const imageCache = new Map();
  const ready = new WeakSet();
  const photos = {};
  const photoPromises = {};

  /* ---------- colour helpers ---------- */
  function rgb(hex) {
    const h = String(hex || '').trim().replace('#', '');
    if (!/^[0-9a-f]{3}([0-9a-f]{3})?$/i.test(h)) return [200, 184, 157];
    const s = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
    return [0, 2, 4].map(i => parseInt(s.slice(i, i + 2), 16));
  }
  function toHex(channels) {
    return '#' + channels.map(v => Math.round(Math.max(0, Math.min(255, v))).toString(16).padStart(2, '0')).join('');
  }
  function mix(hex, other, t) {
    const a = rgb(hex), b = rgb(other);
    return toHex(a.map((v, i) => v + (b[i] - v) * t));
  }
  function luminance(hex) {
    const [r, g, b] = rgb(hex).map(v => { v /= 255; return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  }
  function contrast(a, b) {
    const l1 = luminance(a), l2 = luminance(b);
    return (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);
  }
  function seeded(seed) {
    return () => {
      seed |= 0; seed = seed + 0x6D2B79F5 | 0;
      let t = Math.imul(seed ^ seed >>> 15, 1 | seed);
      t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t;
      return ((t ^ t >>> 14) >>> 0) / 4294967296;
    };
  }
  const TO_LINEAR = new Float32Array(256);
  for (let i = 0; i < 256; i++) { const c = i / 255; TO_LINEAR[i] = c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4); }
  const TO_SRGB = new Uint8ClampedArray(4096);
  for (let i = 0; i < 4096; i++) { const c = i / 4095; TO_SRGB[i] = Math.round(255 * (c <= 0.0031308 ? c * 12.92 : 1.055 * Math.pow(c, 1 / 2.4) - 0.055)); }

  /* ---------- real linen photo ---------- */
  function loadPhoto(shape) {
    const spec = PHOTOS[shape];
    if (!spec || !ASSET_BASE) return Promise.resolve(null);
    if (photoPromises[shape]) return photoPromises[shape];
    photoPromises[shape] = loadImage(ASSET_BASE + spec.file).then(img => {
      if (!img) return null;
      const w = img.naturalWidth, h = img.naturalHeight;
      const c = document.createElement('canvas');
      c.width = w; c.height = h;
      const g = c.getContext('2d', { willReadFrequently: true });
      g.drawImage(img, 0, 0);
      let data;
      try { data = g.getImageData(0, 0, w, h).data; } catch (e) { return null; }
      const n = w * h, light = new Float32Array(n), hist = new Uint32Array(256);
      const spineEnd = Math.round(w * spec.spine);
      for (let i = 0; i < n; i++) {
        const v = data[i * 4];
        light[i] = v;
        if (i % w > spineEnd) hist[v]++;
      }
      // median brightness of the cover = "the fabric's own colour"; everything else is weave and light
      let total = 0; for (let v = 0; v < 256; v++) total += hist[v];
      let acc = 0, median = 200;
      for (let v = 0; v < 256; v++) { acc += hist[v]; if (acc >= total / 2) { median = Math.max(1, v); break; } }
      for (let i = 0; i < n; i++) light[i] = TO_LINEAR[Math.round(light[i])] / TO_LINEAR[median];
      photos[shape] = { w, h, light, spine: spec.spine, shape };
      return photos[shape];
    });
    return photoPromises[shape];
  }
  function loadPhotos() { return Promise.all([loadPhoto('portrait'), loadPhoto('landscape')]); }
  // dye the photographed linen: linear-light multiply keeps the weave; dark shades get a little fibre sheen
  function photoCanvas(hex, photo) {
    if (!photo) return null;
    const key = photo.shape + hex;
    if (photoCache.has(key)) return photoCache.get(key);
    const [r, g, b] = rgb(hex);
    const lr = TO_LINEAR[r], lg = TO_LINEAR[g], lb = TO_LINEAR[b];
    const sheen = 0.1 * (1 - (0.2126 * lr + 0.7152 * lg + 0.0722 * lb));
    const off = document.createElement('canvas');
    off.width = photo.w; off.height = photo.h;
    const c = off.getContext('2d');
    const out = c.createImageData(photo.w, photo.h), px = out.data, light = photo.light;
    const idx = v => (v <= 0 ? 0 : v >= 1 ? 4095 : (v * 4095) | 0);
    for (let i = 0, j = 0; i < light.length; i++, j += 4) {
      const k = light[i], s = k > 1 ? (k - 1) * sheen : 0;
      px[j] = TO_SRGB[idx(lr * k + s)];
      px[j + 1] = TO_SRGB[idx(lg * k + s)];
      px[j + 2] = TO_SRGB[idx(lb * k + s)];
      px[j + 3] = 255;
    }
    c.putImageData(out, 0, 0);
    if (photoCache.size >= 4) photoCache.delete(photoCache.keys().next().value);
    photoCache.set(key, off);
    return off;
  }

  /* ---------- drawn textures (fallback) ---------- */
  function linenCanvas(hex) {
    const key = 'linen:' + hex;
    if (textureCache.has(key)) return textureCache.get(key);
    const size = 144, off = document.createElement('canvas');
    off.width = off.height = size;
    const c = off.getContext('2d'), rand = seeded(7);
    const light = mix(hex, '#ffffff', 0.14), dark = mix(hex, '#000000', 0.1);
    c.fillStyle = hex; c.fillRect(0, 0, size, size);
    for (let y = 0; y < size; y += 3) { c.globalAlpha = 0.12 + rand() * 0.1; c.fillStyle = (y / 3) % 2 ? light : dark; c.fillRect(0, y, size, 1); }
    for (let x = 0; x < size; x += 3) { c.globalAlpha = 0.08 + rand() * 0.08; c.fillStyle = (x / 3) % 2 ? dark : light; c.fillRect(x, 0, 1, size); }
    for (let i = 0; i < 260; i++) { c.globalAlpha = 0.06 + rand() * 0.1; c.fillStyle = rand() < 0.5 ? light : dark; c.fillRect(rand() * size, rand() * size, 2 + rand() * 5, 1); }
    c.globalAlpha = 1;
    // fine grain so the weave reads as fabric rather than a grid
    const grain = c.getImageData(0, 0, size, size), px = grain.data;
    for (let i = 0; i < px.length; i += 4) { const n = (rand() - 0.5) * 14; px[i] += n; px[i + 1] += n; px[i + 2] += n; }
    c.putImageData(grain, 0, 0);
    textureCache.set(key, off);
    return off;
  }
  function satinCanvas(hex) {
    const key = 'satin:' + hex;
    if (textureCache.has(key)) return textureCache.get(key);
    const size = 8, off = document.createElement('canvas');
    off.width = off.height = size;
    const c = off.getContext('2d');
    c.fillStyle = hex; c.fillRect(0, 0, size, size);
    c.lineWidth = 1.4; c.strokeStyle = mix(hex, '#ffffff', 0.3);
    c.beginPath();
    for (let i = -size; i <= size * 2; i += 4) { c.moveTo(i, size + 2); c.lineTo(i + size + 2, -2); }
    c.stroke();
    c.lineWidth = 0.8; c.strokeStyle = mix(hex, '#000000', 0.22);
    c.beginPath();
    for (let i = -size + 2; i <= size * 2; i += 4) { c.moveTo(i, size + 2); c.lineTo(i + size + 2, -2); }
    c.stroke();
    textureCache.set(key, off);
    return off;
  }

  /* ---------- assets ---------- */
  function loadImage(src) {
    if (!src) return Promise.resolve(null);
    if (imageCache.has(src)) return imageCache.get(src);
    const promise = new Promise(resolve => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = () => resolve(null);
      img.src = src;
    });
    imageCache.set(src, promise);
    return promise;
  }
  // motif PNGs often carry empty transparent margins; crop to the stitched artwork so it centres truly
  function trimTransparent(img) {
    if (!img) return img;
    try {
      const w = img.naturalWidth || img.width, h = img.naturalHeight || img.height;
      const c = document.createElement('canvas');
      c.width = w; c.height = h;
      const g = c.getContext('2d', { willReadFrequently: true });
      g.drawImage(img, 0, 0);
      const d = g.getImageData(0, 0, w, h).data;
      let x0 = w, x1 = -1, y0 = h, y1 = -1;
      for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
        if (d[(y * w + x) * 4 + 3] > 10) { if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y; }
      }
      if (x1 < 0 || (x0 === 0 && y0 === 0 && x1 === w - 1 && y1 === h - 1)) return img;
      const out = document.createElement('canvas');
      out.width = x1 - x0 + 1; out.height = y1 - y0 + 1;
      out.getContext('2d').drawImage(c, x0, y0, out.width, out.height, 0, 0, out.width, out.height);
      return out;
    } catch (e) { return img; }
  }
  function ensureFonts(styles) {
    if (!document.fonts || !document.fonts.load) return Promise.resolve();
    return Promise.all(styles.map(s => document.fonts.load(`${s.weight || 400} 40px "${s.family || DEFAULT_FONT}"`).catch(() => null))).then(() => null);
  }

  /* ---------- text ---------- */
  function setFont(ctx, style, size) {
    ctx.font = `${style.weight || 400} ${size}px "${style.family || DEFAULT_FONT}", "${DEFAULT_FONT}", Georgia, serif`;
  }
  function transformText(text, mode) {
    return mode === 'uppercase' ? text.toUpperCase() : mode === 'lowercase' ? text.toLowerCase() : text;
  }
  function measure(ctx, text, spacing) {
    if (!spacing) return ctx.measureText(text).width;
    const chars = [...text];
    return chars.reduce((sum, ch) => sum + ctx.measureText(ch).width, 0) + spacing * Math.max(0, chars.length - 1);
  }
  function drawText(ctx, text, x, y, spacing, mode) {
    if (!spacing) {
      ctx.textAlign = 'center';
      if (mode === 'stroke') ctx.strokeText(text, x, y); else ctx.fillText(text, x, y);
      return;
    }
    ctx.textAlign = 'left';
    const chars = [...text];
    const widths = chars.map(ch => ctx.measureText(ch).width);
    let cx = x - (widths.reduce((a, b) => a + b, 0) + spacing * (chars.length - 1)) / 2;
    chars.forEach((ch, i) => {
      if (mode === 'stroke') ctx.strokeText(ch, cx, y); else ctx.fillText(ch, cx, y);
      cx += widths[i] + spacing;
    });
  }
  // thread colour laid into a white-on-transparent mask as satin stitches: slanted thread columns,
  // a raised bevel (light top-left, shade bottom-right) and a soft light from above
  function satinLayer(mask, W, H, hex, size, seed, top, bottom) {
    const t = layer(W, H), g = t.getContext('2d');
    g.fillStyle = hex; g.fillRect(0, 0, W, H);
    const p = Math.max(3, size * 0.068), rand = seeded(seed);
    const slant = Math.tan(Math.PI * 0.2) * H; // columns lean like hand satin stitch
    const light = mix(hex, '#ffffff', 0.42), dark = mix(hex, '#000000', 0.34);
    g.lineCap = 'round';
    for (let i = -slant - p; i < W + p; i += p) {
      const j = (rand() - 0.5) * p * 0.25;
      g.globalAlpha = 0.4 + rand() * 0.25; g.strokeStyle = light; g.lineWidth = p * 0.4;
      g.beginPath(); g.moveTo(i + j, H); g.lineTo(i + j + slant, 0); g.stroke();
      g.globalAlpha = 0.35 + rand() * 0.2; g.strokeStyle = dark; g.lineWidth = p * 0.2;
      g.beginPath(); g.moveTo(i + j + p * 0.5, H); g.lineTo(i + j + p * 0.5 + slant, 0); g.stroke();
    }
    g.globalAlpha = 1;
    // soft light from above across the letters
    const shade = g.createLinearGradient(0, top, 0, bottom);
    shade.addColorStop(0, 'rgba(255,255,255,.14)'); shade.addColorStop(1, 'rgba(0,0,0,.12)');
    g.fillStyle = shade; g.fillRect(0, 0, W, H);

    // raised edges: highlight on the top-left rim, shade on the bottom-right rim
    const d = Math.max(1, size * 0.03);
    const rim = (dx, dy, colour) => {
      const e = layer(W, H), c = e.getContext('2d');
      c.drawImage(mask, 0, 0);
      c.globalCompositeOperation = 'destination-out'; c.drawImage(mask, dx, dy);
      c.globalCompositeOperation = 'source-in'; c.fillStyle = colour; c.fillRect(0, 0, W, H);
      try { g.filter = `blur(${Math.max(0.5, d * 0.6)}px)`; } catch (err) { /* older browsers: hard edge */ }
      g.drawImage(e, 0, 0);
      g.filter = 'none';
    };
    rim(d, d, 'rgba(255,255,255,.55)');
    rim(-d, -d, 'rgba(0,0,0,.42)');
    g.globalCompositeOperation = 'destination-in';
    g.drawImage(mask, 0, 0);
    g.globalCompositeOperation = 'source-over';
    return t;
  }
  function stitchedLayer(ctx, t, x, y, size, alpha) {
    ctx.save();
    ctx.globalAlpha = alpha == null ? 1 : alpha;
    ctx.shadowColor = 'rgba(45,35,25,.42)';
    ctx.shadowBlur = size * 0.06;
    ctx.shadowOffsetX = size * 0.012;
    ctx.shadowOffsetY = size * 0.03;
    ctx.drawImage(t, x, y);
    ctx.restore();
  }
  // satin-stitched lettering
  function layer(w, h) { const c = document.createElement('canvas'); c.width = w; c.height = h; return c; }
  function stitched(ctx, text, x, y, style, size, hex, alpha) {
    setFont(ctx, style, size);
    const spacing = (Number(style.spacing) || 0) * size;
    const tw = measure(ctx, text, spacing);
    const padX = size * 0.35, above = size * 1.15, below = size * 0.6;
    const W = Math.max(1, Math.ceil(tw + padX * 2)), H = Math.max(1, Math.ceil(above + below));
    const ox = x - W / 2, oy = y - above, bx = W / 2, by = above;

    // letter shape, slightly fattened like a satin column
    const mask = layer(W, H), m = mask.getContext('2d');
    setFont(m, style, size); m.textBaseline = 'alphabetic';
    m.fillStyle = '#fff'; m.strokeStyle = '#fff'; m.lineJoin = 'round';
    m.lineWidth = Math.max(0.8, size * (Number(style.stroke) || 0.028));
    drawText(m, text, bx, by, spacing, 'fill');
    drawText(m, text, bx, by, spacing, 'stroke');

    const t = satinLayer(mask, W, H, hex, size, text.length * 131 + Math.round(size), by - size, by + size * 0.2);
    stitchedLayer(ctx, t, ox, oy, size, alpha);
  }
  // an outline heart stitched in the same thread, e.g. between two names (top = top of the lobes)
  function heartPath(c, cx, top, hh) {
    const w = hh * 1.08;
    c.beginPath();
    c.moveTo(cx, top + hh * 0.3);
    c.bezierCurveTo(cx, top, cx - w / 2, top, cx - w / 2, top + hh * 0.3);
    c.bezierCurveTo(cx - w / 2, top + hh * 0.6, cx, top + hh * 0.78, cx, top + hh);
    c.bezierCurveTo(cx, top + hh * 0.78, cx + w / 2, top + hh * 0.6, cx + w / 2, top + hh * 0.3);
    c.bezierCurveTo(cx + w / 2, top, cx, top, cx, top + hh * 0.3);
    c.closePath();
  }
  function stitchedHeart(ctx, cx, top, hh, hex, alpha, size) {
    const pad = hh * 0.35, W = Math.max(1, Math.ceil(hh * 1.08 + pad * 2)), H = Math.max(1, Math.ceil(hh + pad * 2));
    const mask = layer(W, H), m = mask.getContext('2d');
    m.strokeStyle = '#fff'; m.lineJoin = 'round'; m.lineCap = 'round';
    m.lineWidth = Math.max(1.2, size * 0.055);
    heartPath(m, W / 2, pad, hh); m.stroke();
    const t = satinLayer(mask, W, H, hex, size, 29 + Math.round(hh), pad - hh * 0.1, pad + hh);
    stitchedLayer(ctx, t, cx - W / 2, top - pad, size, alpha);
  }
  // single-colour artwork is stored as a grey stitch-shading layer with alpha; dye it with the thread
  const tintCache = new WeakMap();
  function tintedArt(img, hex) {
    let per = tintCache.get(img);
    if (!per) { per = new Map(); tintCache.set(img, per); }
    if (per.has(hex)) return per.get(hex);
    const w = img.width, h = img.height, c = layer(w, h), g = c.getContext('2d');
    g.fillStyle = hex; g.fillRect(0, 0, w, h);
    try { g.globalCompositeOperation = 'soft-light'; g.drawImage(img, 0, 0); } catch (e) { /* flat thread colour */ }
    g.globalCompositeOperation = 'destination-in'; g.drawImage(img, 0, 0);
    g.globalCompositeOperation = 'source-over';
    if (per.size >= 4) per.delete(per.keys().next().value);
    per.set(hex, c);
    return c;
  }
  function fit(ctx, style, text, maxWidth, base, min) {
    let size = base;
    while (size >= min) {
      setFont(ctx, style, size);
      if (measure(ctx, text, (Number(style.spacing) || 0) * size) <= maxWidth) return size;
      size -= Math.max(1, base * 0.03);
    }
    return null;
  }
  function splitLines(text) {
    const lower = text.toLowerCase();
    for (const sep of [' & ', ' and ', ' + ']) {
      const i = lower.indexOf(sep);
      if (i > 0) return [text.slice(0, i).trim(), text.slice(i + 1).trim()];
    }
    const mid = text.length / 2;
    let best = -1;
    for (let i = 0; i < text.length; i++) if (text[i] === ' ' && (best < 0 || Math.abs(i - mid) < Math.abs(best - mid))) best = i;
    return best > 0 ? [text.slice(0, best).trim(), text.slice(best + 1).trim()] : [text];
  }

  /* ---------- drawing ---------- */
  function roundRect(ctx, x, y, w, h, r) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
  }
  function ornament(ctx, cx, cy, length, hex) {
    ctx.save();
    ctx.strokeStyle = hex; ctx.fillStyle = hex; ctx.lineCap = 'round';
    ctx.lineWidth = Math.max(1.6, length * 0.016);
    ctx.shadowColor = 'rgba(45,35,25,.35)'; ctx.shadowBlur = 2; ctx.shadowOffsetY = 1.2;
    ctx.setLineDash([length * 0.05, length * 0.035]);
    ctx.beginPath();
    ctx.moveTo(cx - length / 2, cy); ctx.lineTo(cx - length * 0.09, cy);
    ctx.moveTo(cx + length * 0.09, cy); ctx.lineTo(cx + length / 2, cy);
    ctx.stroke();
    ctx.setLineDash([]);
    const d = length * 0.045;
    ctx.beginPath();
    ctx.moveTo(cx, cy - d); ctx.lineTo(cx + d, cy); ctx.lineTo(cx, cy + d); ctx.lineTo(cx - d, cy);
    ctx.closePath(); ctx.fill();
    ctx.restore();
  }
  // how big the canvas really is on screen, in device pixels, as a share of the full drawing
  function displayScale(canvas, W, H) {
    const box = canvas.parentElement;
    let s = 0;
    if (box && box.classList.contains('preview-stage') && box.clientWidth && box.clientHeight) s = Math.min(box.clientWidth / W, box.clientHeight / H);
    else if (canvas.clientWidth) s = canvas.clientWidth / W;
    if (!s) return 1;
    return Math.min(1, s * (window.devicePixelRatio || 1));
  }
  // draw at full size, then step down by halves to the on-screen size: keeps the fine linen weave
  // from turning into a coarse moire grid when the browser shrinks it
  let paintBuffer = null;
  function draw(canvas, o) {
    const [W, H] = SHAPES[o.shape] || SHAPES.portrait;
    const k = displayScale(canvas, W, H);
    if (k > 0.97) { paint(canvas, o); return; }
    const full = paintBuffer || (paintBuffer = document.createElement('canvas'));
    paint(full, o);
    const tw = Math.max(1, Math.round(W * k)), th = Math.max(1, Math.round(H * k));
    let src = full, cw = W, ch = H;
    while (cw / 2 >= tw) {
      const t = document.createElement('canvas');
      t.width = Math.round(cw / 2); t.height = Math.round(ch / 2);
      const g = t.getContext('2d');
      g.imageSmoothingEnabled = true; g.imageSmoothingQuality = 'high';
      g.drawImage(src, 0, 0, t.width, t.height);
      src = t; cw = t.width; ch = t.height;
    }
    if (canvas.width !== tw) canvas.width = tw;
    if (canvas.height !== th) canvas.height = th;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, tw, th);
    ctx.imageSmoothingEnabled = true; ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(src, 0, 0, tw, th);
  }
  function paint(canvas, o) {
    const [W, H] = SHAPES[o.shape] || SHAPES.portrait;
    if (canvas.width !== W) canvas.width = W;
    if (canvas.height !== H) canvas.height = H;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, W, H);
    const linen = /^#/.test(o.linen || '') ? o.linen : '#c8b89d';
    const thread = /^#/.test(o.thread || '') ? o.thread : '#8A9A7E';
    const pad = Math.round(W * 0.07);
    const x = pad, y = pad, w = W - pad * 2, h = H - pad * 2, r = w * 0.012;
    const lift = w * 0.013;

    // page block behind the cover
    ctx.save();
    ctx.fillStyle = '#f4efe4';
    roundRect(ctx, x + lift, y + lift, w, h, r); ctx.fill();
    ctx.strokeStyle = 'rgba(59,53,48,.2)'; ctx.lineWidth = 1; ctx.stroke();
    ctx.strokeStyle = 'rgba(59,53,48,.14)';
    for (let i = 1; i <= 4; i++) {
      const off = lift * i / 4;
      ctx.beginPath();
      ctx.moveTo(x + w + off, y + off + r); ctx.lineTo(x + w + off, y + h + off - r);
      ctx.moveTo(x + off + r, y + h + off); ctx.lineTo(x + w + off - r, y + h + off);
      ctx.stroke();
    }
    ctx.restore();

    // cover: shadow, base colour, then the dyed linen photo (or drawn weave), light, spine
    ctx.save();
    ctx.shadowColor = 'rgba(59,53,48,.3)'; ctx.shadowBlur = W * 0.03; ctx.shadowOffsetY = W * 0.008;
    ctx.fillStyle = linen;
    roundRect(ctx, x, y, w, h, r); ctx.fill();
    ctx.restore();
    const wideCover = w > h * 1.05;
    // the photo shot in this format; a horizontal book can fall back to the vertical photo, turned
    const photo = (wideCover && photos.landscape) || photos.portrait || null;
    const dyed = photoCanvas(linen, photo);
    const sw = !dyed ? w * 0.06 : (photo.shape === 'landscape' || !wideCover) ? w * photo.spine : w * 0.04;
    ctx.save();
    roundRect(ctx, x, y, w, h, r); ctx.clip();
    if (dyed) {
      // real spine to the spine strip, real cover cropped to fill the rest
      const spineSrc = Math.round(photo.w * photo.spine);
      const spineH = Math.min(photo.h, h * spineSrc / sw);
      ctx.drawImage(dyed, 0, (photo.h - spineH) / 2, spineSrc, spineH, x, y, sw, h);
      const srcW = photo.w - spineSrc, srcH = photo.h, dW = w - sw, dH = h;
      if (dW > dH && photo.shape !== 'landscape') {
        // horizontal book without its own photo: the same linen, turned a quarter so the weave keeps its true scale
        const scale = Math.max(dW / srcH, dH / srcW), cw = dW / scale, ch = dH / scale;
        ctx.save();
        ctx.translate(x + sw + dW / 2, y + dH / 2);
        ctx.rotate(Math.PI / 2);
        ctx.drawImage(dyed, spineSrc + (srcW - ch) / 2, (srcH - cw) / 2, ch, cw, -dH / 2, -dW / 2, dH, dW);
        ctx.restore();
      } else {
        const scale = Math.max(dW / srcW, dH / srcH), cw = dW / scale, ch = dH / scale;
        ctx.drawImage(dyed, spineSrc + (srcW - cw) / 2, (srcH - ch) / 2, cw, ch, x + sw, y, dW, dH);
      }
    } else {
      ctx.fillStyle = ctx.createPattern(linenCanvas(linen), 'repeat');
      ctx.fillRect(x, y, w, h);
      const light = ctx.createRadialGradient(x + w * 0.45, y + h * 0.4, w * 0.2, x + w * 0.5, y + h * 0.5, w * 0.95);
      light.addColorStop(0, 'rgba(255,255,255,.07)');
      light.addColorStop(0.6, 'rgba(0,0,0,0)');
      light.addColorStop(1, 'rgba(0,0,0,.15)');
      ctx.fillStyle = light; ctx.fillRect(x, y, w, h);
      const spine = ctx.createLinearGradient(x, 0, x + sw, 0);
      spine.addColorStop(0, 'rgba(0,0,0,.17)');
      spine.addColorStop(0.55, 'rgba(0,0,0,.04)');
      spine.addColorStop(1, 'rgba(255,255,255,.1)');
      ctx.fillStyle = spine; ctx.fillRect(x, y, sw, h);
      ctx.strokeStyle = 'rgba(0,0,0,.1)'; ctx.lineWidth = 1.2;
      ctx.beginPath(); ctx.moveTo(x + sw, y); ctx.lineTo(x + sw, y + h); ctx.stroke();
    }
    ctx.restore();
    ctx.save();
    roundRect(ctx, x + 0.5, y + 0.5, w - 1, h - 1, r);
    ctx.strokeStyle = 'rgba(0,0,0,.15)'; ctx.lineWidth = 1; ctx.stroke();
    ctx.restore();

    // optional stitched border
    if (o.border) {
      ctx.save();
      const inset = w * 0.075;
      ctx.setLineDash([w * 0.012, w * 0.008]);
      ctx.lineWidth = Math.max(1.6, w * 0.003);
      ctx.strokeStyle = thread;
      ctx.shadowColor = 'rgba(45,35,25,.35)'; ctx.shadowBlur = 2; ctx.shadowOffsetY = 1.5;
      ctx.strokeRect(x + sw + inset * 0.6, y + inset, w - sw - inset * 1.6, h - inset * 2);
      ctx.restore();
    }

    // per-design layout (26 Sep 2026): the product's own composition instead of the generic stack
    if (o.layout && Array.isArray(o.layout.rows) && o.layout.rows.length) {
      paintLayout(ctx, o, x, y, w, h, sw, thread);
      return;
    }

    // composition column (to the right of the spine)
    const cx = x + sw + (w - sw) / 2;
    const maxWidth = (w - sw) * 0.78;
    const wide = w > h * 1.05;

    // measure the whole embroidery block first (motif, names, date) so it can sit
    // exactly in the middle of the front cover, whatever the names and format
    const style = o.style || {};
    const typed = String(o.names || '').trim();
    const namesText = transformText(typed || o.placeholder || 'Your names', style.transform);
    const alpha = typed ? 1 : 0.42;
    const base = (w - sw) * (Number(style.scale) || 0.1) * 1.1;
    let lines = [namesText];
    let size = fit(ctx, style, namesText, maxWidth, base, base * 0.6);
    if (size == null) {
      lines = splitLines(namesText);
      const sizes = lines.map(line => fit(ctx, style, line, maxWidth, base, base * 0.35) || base * 0.35);
      size = Math.min(...sizes);
    }
    const lineHeight = size * 1.12;
    const namesH = (lines.length - 1) * lineHeight + size * 0.72; // cap height of the lettering
    const date = String(o.date || '').trim();
    const dateText = transformText(date, DATE_STYLE.transform);
    const dateSize = date ? (fit(ctx, DATE_STYLE, dateText, maxWidth, (w - sw) * 0.048, (w - sw) * 0.026) || (w - sw) * 0.026) : 0;
    const dateGap = h * 0.09;

    let motifW = 0, motifH = 0;
    const ornamentLen = (w - sw) * (wide ? 0.2 : 0.24);
    if (o.motifImage) {
      const img = o.motifImage;
      // horizontal covers have a wider, shorter stitching area
      const boxW = (w - sw) * (wide ? 0.8 : 0.66), boxH = h * (wide ? 0.4 : 0.32);
      const s = Math.min(boxW / img.width, boxH / img.height);
      motifW = img.width * s; motifH = img.height * s;
    } else {
      motifH = ornamentLen * 0.09;
    }
    const motifGap = h * (o.motifImage ? (wide ? 0.06 : 0.055) : 0.1);
    const blockH = motifH + motifGap + namesH + (date ? dateGap : 0);
    const top = y + (h - blockH) / 2;

    // motif (or a small stitched ornament)
    if (o.motifImage) {
      ctx.save();
      ctx.shadowColor = 'rgba(45,35,25,.25)'; ctx.shadowBlur = W * 0.006; ctx.shadowOffsetY = W * 0.003;
      ctx.drawImage(o.motifImage, cx - motifW / 2, top, motifW, motifH);
      ctx.restore();
    } else {
      ornament(ctx, cx, top + motifH / 2, ornamentLen, thread);
    }

    // names
    const namesCenter = top + motifH + motifGap + namesH / 2;
    const baseline = namesCenter - (lines.length - 1) * lineHeight / 2 + size * 0.36;
    lines.forEach((line, i) => stitched(ctx, line, cx, baseline + i * lineHeight, style, size, thread, alpha));
    const lastBaseline = baseline + (lines.length - 1) * lineHeight;

    // date
    if (date) stitched(ctx, dateText, cx, lastBaseline + dateGap, DATE_STYLE, dateSize, thread, 1);
  }

  /* ---------- per-design layouts (26 Sep 2026) ----------
     A product can carry its own composition in the metafield custom.preview_layout (JSON):
       { "motif": { "src": "https://…/artwork.png", "tint": true },
         "rows": [
           { "t": "text", "v": "Our journey begins", "cap": 0.083, "max": 0.7 },
           { "t": "motif", "w": 0.77, "ratio": 0.72, "gap": 0.05,
             "slots": [ { "x": 0.39, "y": 0.81, "cap": 0.13, "max": 0.09 } ] },
           { "t": "names", "join": "heart", "cap": 0.072, "max": 0.62, "gap": 0.023 },
           { "t": "date", "prefix": "EST. ", "cap": 0.03, "font": { "family": "Karla", "weight": 400, "spacing": 0.04 } } ],
         "sample": { "names": "Taylor & Travis", "date": "11.11.2026" } }
     Rows stack from the top; "gap" is the space above a row, "cap" the height of a capital letter
     and "max" the widest a line may run, all as shares of the front cover's width. The block is
     centred on the cover and shrunk to fit when the book is turned to a shorter format.
     t = "text" is fixed wording of the design, "names" / "date" come from the shopper (faded sample
     text until they type). Text uses the shopper's lettering style unless the row names its own
     "font". "join": "heart" stitches a heart between two names; "prefix" is added unless typed.
     "tint": true dyes a greyscale artwork with the chosen thread (single-colour designs); false
     keeps the artwork's own colours (multi-colour designs). "slots" place the shopper's initials
     inside the artwork: x / y = centre of the letter as a share of the artwork box, cap = letter
     height as a share of the artwork height. */
  function parseLayout(raw) {
    if (!raw) return null;
    try {
      const l = typeof raw === 'string' ? JSON.parse(raw) : raw;
      return l && Array.isArray(l.rows) && l.rows.length ? l : null;
    } catch (e) { return null; }
  }
  function layoutFonts(layout) {
    if (!layout) return [];
    return layout.rows.filter(r => r && r.font && typeof r.font === 'object').map(r => r.font);
  }
  const capCache = new Map();
  function capRatio(ctx, style) {
    const key = `${style.weight || 400} ${style.family || DEFAULT_FONT}`;
    if (capCache.has(key)) return capCache.get(key);
    setFont(ctx, style, 100);
    const m = ctx.measureText('H');
    const r = m.actualBoundingBoxAscent > 1 ? m.actualBoundingBoxAscent / 100 : 0.7;
    let ready = true;
    try { ready = !document.fonts || document.fonts.check(`${style.weight || 400} 40px "${style.family || DEFAULT_FONT}"`); } catch (e) { ready = true; }
    if (ready) capCache.set(key, r); // fallback-font metrics are not kept
    return r;
  }
  function initialsOf(text) {
    const parts = String(text || '').split(/[^\p{L}\p{N}]+/u).filter(Boolean).filter((part, i, all) => all.length < 3 || !/^and$/i.test(part));
    if (parts.length >= 2) return [parts[0], parts[1]].map(p => [...p][0].toUpperCase());
    if (parts.length === 1) {
      const chars = [...parts[0]];
      return chars.length <= 3 ? [chars[0] || '', chars[1] || ''].map(c => c.toUpperCase()) : [chars[0].toUpperCase(), ''];
    }
    return ['', ''];
  }
  function splitPair(text) {
    const parts = String(text).split(/\s*(?:&|\+|♥|❤|\band\b)\s*/i).map(p => p.trim()).filter(Boolean);
    return parts.length >= 2 ? [parts[0], parts.slice(1).join(' ')] : null;
  }
  function paintLayout(ctx, o, x, y, w, h, sw, thread) {
    const L = o.layout, U = w - sw, cx = x + sw + U / 2;
    const chosen = o.style || {};
    const faded = 0.6;
    const sample = L.sample || {};
    const namesTyped = String(o.names || '').trim(), dateTyped = String(o.date || '').trim();
    const namesVal = namesTyped || String(sample.names || o.placeholder || '').trim();
    const dateVal = dateTyped || String(sample.date || o.datePlaceholder || '').trim();
    const spacingOf = (style, size) => (Number(style.spacing) || 0) * size;

    // 1) measure every row at full size
    const rows = L.rows.map((r, i) => {
      const gap = i ? (Number(r.gap) || 0) * U : 0;
      if (r.t === 'motif') {
        const img = o.motifImage;
        const ratio = img && img.width ? img.height / img.width : (Number(r.ratio) || 0.6);
        const mw = (Number(r.w) || 0.7) * U;
        return { r, gap, kind: 'motif', mw, mh: mw * ratio, height: mw * ratio };
      }
      const style = r.font && typeof r.font === 'object' ? r.font : chosen;
      let text = '', alpha = 1;
      if (r.t === 'names') { text = namesVal; alpha = namesTyped ? 1 : faded; }
      else if (r.t === 'date') {
        text = dateVal; alpha = dateTyped ? 1 : faded;
        const prefix = String(r.prefix || '');
        if (prefix && text && !text.toLowerCase().startsWith(prefix.trim().toLowerCase())) text = prefix + text;
      } else text = String(r.v || '');
      text = transformText(text.trim(), style.transform);
      if (!text) return { r, gap: 0, kind: 'empty', height: 0 };
      const cr = capRatio(ctx, style);
      let size = (Number(r.cap) || 0.06) * U / cr;
      const maxW = (Number(r.max) || 0.8) * U;
      const pair = r.join === 'heart' ? splitPair(text) : null;
      const widthAt = sz => {
        setFont(ctx, style, sz);
        const sp = spacingOf(style, sz);
        return pair ? measure(ctx, pair[0], sp) + measure(ctx, pair[1], sp) + sz * cr * 1.36 : measure(ctx, text, sp);
      };
      let lines = [text];
      const wdt = widthAt(size);
      if (wdt > maxW) {
        const shrunk = size * maxW / wdt;
        if (shrunk >= size * 0.6 || pair || r.t !== 'names') size = Math.max(shrunk, size * 0.3);
        else {
          lines = splitLines(text);
          setFont(ctx, style, size);
          const widest = Math.max(...lines.map(line => measure(ctx, line, spacingOf(style, size))));
          if (widest > maxW) size *= maxW / widest;
        }
      }
      const capPx = size * cr, lead = capPx * 1.75;
      return { r, gap, kind: 'text', style, text, pair, lines, size, capPx, lead, alpha, height: capPx + (lines.length - 1) * lead };
    });

    // 2) fit the block to the cover and centre it
    const total = rows.reduce((sum, row) => sum + row.gap + row.height, 0);
    const room = h * (Number(L.fill) || 0.84);
    const k = total > room ? room / total : 1;
    let cursor = y + (h - total * k) / 2 + (Number(L.dy) || 0) * h;

    // 3) draw
    rows.forEach(row => {
      cursor += row.gap * k;
      if (row.kind === 'motif') {
        const mw = row.mw * k, mh = row.mh * k, mx = cx - mw / 2, my = cursor;
        const img = o.motifImage;
        if (img) {
          const art = L.motif && L.motif.tint ? tintedArt(img, thread) : img;
          ctx.save();
          ctx.shadowColor = 'rgba(45,35,25,.3)'; ctx.shadowBlur = Math.max(1, mw * 0.005);
          ctx.shadowOffsetX = mw * 0.0015; ctx.shadowOffsetY = mw * 0.0035;
          ctx.drawImage(art, mx, my, mw, mh);
          ctx.restore();
        }
        const slots = Array.isArray(row.r.slots) ? row.r.slots : [];
        if (slots.length) {
          const letters = initialsOf(namesVal);
          const cr = capRatio(ctx, chosen);
          slots.forEach((slot, i) => {
            const letter = transformText(letters[i] || '', chosen.transform);
            if (!letter) return;
            let size = (Number(slot.cap) || 0.12) * mh / cr;
            setFont(ctx, chosen, size);
            const lw = measure(ctx, letter, 0), maxW = (Number(slot.max) || 0.09) * mw;
            if (lw > maxW) size *= maxW / lw;
            const capPx = size * cr;
            stitched(ctx, letter, mx + (Number(slot.x) || 0.5) * mw, my + (Number(slot.y) || 0.5) * mh + capPx / 2, chosen, size, thread, namesTyped ? 1 : faded);
          });
        }
        cursor += mh;
        return;
      }
      if (row.kind !== 'text') return;
      const size = row.size * k, capPx = row.capPx * k, lead = row.lead * k;
      if (row.pair) {
        setFont(ctx, row.style, size);
        const sp = spacingOf(row.style, size);
        const w1 = measure(ctx, row.pair[0], sp), w2 = measure(ctx, row.pair[1], sp);
        const hh = capPx * 0.92, hw = hh * 1.08, g = capPx * 0.14;
        const start = cx - (w1 + g + hw + g + w2) / 2, base = cursor + capPx;
        stitched(ctx, row.pair[0], start + w1 / 2, base, row.style, size, thread, row.alpha);
        stitchedHeart(ctx, start + w1 + g + hw / 2, base - hh, hh, thread, row.alpha, size);
        stitched(ctx, row.pair[1], start + w1 + g + hw + g + w2 / 2, base, row.style, size, thread, row.alpha);
      } else {
        row.lines.forEach((line, i) => stitched(ctx, line, cx, cursor + capPx + i * lead, row.style, size, thread, row.alpha));
      }
      cursor += capPx + (row.lines.length - 1) * lead;
    });
  }

  /* ---------- product page ---------- */
  function reduceMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }
  function withWord(name, word) {
    if (!name) return '';
    return name.toLowerCase().includes(word) ? name : `${name} ${word}`;
  }
  function fitSamples(root) {
    root.querySelectorAll('.style-sample').forEach(sample => {
      if (!sample.dataset.baseSize) sample.dataset.baseSize = getComputedStyle(sample).fontSize;
      let size = parseFloat(sample.dataset.baseSize);
      sample.style.fontSize = '';
      let guard = 0;
      while (sample.scrollWidth > sample.clientWidth + 1 && size > 11 && guard++ < 14) {
        size -= 1;
        sample.style.fontSize = size + 'px';
      }
    });
  }
  function readStyle(input) {
    if (!input) return {};
    const d = input.dataset;
    return { family: d.font, weight: d.weight, spacing: Number(d.spacing) || 0, transform: d.transform, scale: Number(d.scale) || 0, stroke: Number(d.stroke) || 0, label: input.value };
  }
  function copyText(text) {
    if (navigator.clipboard && window.isSecureContext) return navigator.clipboard.writeText(text);
    return new Promise((resolve, reject) => {
      const field = document.createElement('textarea');
      field.value = text; field.setAttribute('readonly', ''); field.style.position = 'fixed'; field.style.opacity = '0';
      document.body.appendChild(field); field.select();
      let ok = false;
      try { ok = document.execCommand('copy'); } catch (e) { ok = false; }
      field.remove();
      ok ? resolve() : reject(new Error('copy failed'));
    });
  }
  function initProduct(section) {
    if (ready.has(section)) return;
    const form = section.querySelector('.personalize-form');
    const card = section.querySelector('[data-preview-card]');
    if (!form || !card) return;
    ready.add(section);
    const canvas = card.querySelector('[data-preview-canvas]');
    const names = form.querySelector('[data-names-input]');
    const date = form.querySelector('[data-date-input]');
    const hint = form.querySelector('[data-names-hint]');
    const caption = card.querySelector('[data-preview-caption]');
    const warning = card.querySelector('[data-preview-warning]');
    const proof = form.querySelector('[data-proof-link]');
    let motifImage = null, motifWide = null, lastOptions = null;
    let lastShape = '';
    const layout = parseLayout(card.dataset.layout);

    const checked = name => form.querySelector(`[name="properties[${name}]"]:checked`);
    function state() {
      const linen = checked('Linen color'), thread = checked('Thread color'), styleInput = checked('Lettering style');
      const format = checked('Book format');
      const motif = form.querySelector('[name="properties[Embroidery motif]"]');
      return {
        linen: linen?.dataset.hex, linenName: linen?.value,
        thread: thread?.dataset.hex, threadName: thread?.value,
        style: readStyle(styleInput),
        names: names?.value || '', date: date?.value || '',
        motifName: motif?.value,
        format: format?.value, shape: format?.dataset.shape || card.dataset.shape, border: card.dataset.border === 'true',
        placeholder: names?.placeholder, datePlaceholder: date?.placeholder,
        layout
      };
    }
    function buildLink(s) {
      const url = new URL(location.origin + location.pathname);
      const set = (key, value) => { if (value) url.searchParams.set(key, value); };
      const variants = form.querySelector('[data-variant-select]');
      if (variants && !variants.hidden && variants.value) set('variant', variants.value);
      // 'book', not 'format': Shopify reserves ?format= and answers Not Found
      set('book', s.format); set('linen', s.linenName); set('motif', s.motifName); set('style', s.style.label); set('thread', s.threadName);
      set('names', s.names.trim()); set('date', s.date.trim());
      return url.toString();
    }
    let timer = 0;
    function schedule() { clearTimeout(timer); timer = setTimeout(render, 30); }
    function render() {
      const s = state();
      const motif = s.shape === 'landscape' && motifWide ? motifWide : motifImage;
      lastOptions = Object.assign({}, s, { motifImage: motif });
      try { draw(canvas, lastOptions); } catch (e) { return; }
      card.hidden = false;
      card.dataset.currentShape = s.shape || 'portrait';
      if (lastShape && s.shape !== lastShape && !reduceMotion()) {
        canvas.classList.remove('is-turning'); void canvas.offsetWidth; canvas.classList.add('is-turning');
      }
      lastShape = s.shape;
      const selectedFormat = form.querySelector('[data-selected-format]');
      if (selectedFormat) selectedFormat.textContent = s.format || '';
      if (caption) caption.textContent = [s.format, withWord(s.linenName, 'linen'), withWord(s.threadName, 'thread'), s.style.label].filter(Boolean).join(' · ');
      const selectedThread = form.querySelector('[data-selected-thread]');
      if (selectedThread) selectedThread.textContent = s.threadName || '';
      const selectedStyle = form.querySelector('[data-selected-style]');
      if (selectedStyle) selectedStyle.textContent = s.style.label || '';
      // a tone-on-tone design shown in its own colours is meant to be subtle: no warning for it
      const asDesigned = !!(layout && layout.defaults && s.linenName === layout.defaults.linen && s.threadName === layout.defaults.thread);
      if (warning) warning.hidden = asDesigned || !(s.linen && s.thread && contrast(s.linen, s.thread) < 1.35);
      if (proof) proof.value = buildLink(s);
    }

    // keep to characters the embroidery fonts carry
    const disallowed = /[^\p{L}\p{M}\p{N} &'’.,\-!?()/]/gu;
    function clean(input) {
      const value = input.value, cleaned = value.replace(disallowed, '');
      if (cleaned === value) return;
      const cursor = Math.max(0, (input.selectionStart || 0) - (value.length - cleaned.length));
      input.value = cleaned;
      try { input.setSelectionRange(cursor, cursor); } catch (e) { /* not supported for this input */ }
      if (hint) {
        hint.hidden = false;
        clearTimeout(hint._timer);
        hint._timer = setTimeout(() => { hint.hidden = true; }, 4000);
      }
    }
    names?.addEventListener('input', () => clean(names));
    date?.addEventListener('input', () => clean(date));
    form.addEventListener('input', schedule);
    form.addEventListener('change', schedule);

    // pre-fill from a shared / proof link
    const params = new URLSearchParams(location.search);
    const pick = (name, value) => {
      if (!value) return;
      form.querySelectorAll(`[name="properties[${name}]"]`).forEach(el => {
        if (el.type === 'radio') { if (el.value.toLowerCase() === value.toLowerCase()) el.checked = true; }
        else if (el.tagName === 'SELECT') { const option = [...el.options].find(op => op.value.toLowerCase() === value.toLowerCase()); if (option) el.value = option.value; }
        else if (el.type !== 'checkbox' && el.type !== 'hidden') { el.value = value.slice(0, el.maxLength > 0 ? el.maxLength : 200); }
      });
    };
    pick('Book format', params.get('book')); pick('Linen color', params.get('linen')); pick('Embroidery motif', params.get('motif'));
    pick('Lettering style', params.get('style')); pick('Thread color', params.get('thread'));
    pick('Names', params.get('names')); pick('Date', params.get('date'));
    if (params.has('names') || params.has('linen') || params.has('book')) form.dispatchEvent(new Event('input', { bubbles: true }));

    // view larger / share
    card.querySelector('[data-preview-zoom]')?.addEventListener('click', () => {
      const dialog = document.getElementById(card.dataset.zoomDialog);
      const image = dialog?.querySelector('img');
      if (!dialog || !image) return;
      // "View larger" gets a full-size drawing, not the on-screen one
      const big = document.createElement('canvas');
      try { paint(big, lastOptions || state()); image.src = big.toDataURL('image/png'); } catch (e) { return; }
      image.width = big.width; image.height = big.height;
      if (typeof dialog.showModal === 'function') dialog.showModal();
    });
    card.querySelector('[data-preview-share]')?.addEventListener('click', () => {
      const status = card.querySelector('[data-preview-share-status]');
      const link = buildLink(state());
      copyText(link).then(() => { if (status) status.textContent = 'Link copied — send it to anyone who should see it.'; })
        .catch(() => { if (status) status.textContent = link; });
      if (status) { clearTimeout(status._timer); status._timer = setTimeout(() => { status.textContent = ''; }, 6000); }
    });

    const styles = [...form.querySelectorAll('[name="properties[Lettering style]"]')].map(readStyle);
    const refresh = () => { render(); fitSamples(form); };
    refresh();
    ensureFonts(styles.concat([DATE_STYLE], layoutFonts(layout))).then(refresh);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(refresh);
    window.addEventListener('resize', () => { fitSamples(form); schedule(); });
    loadImage(card.dataset.motif).then(img => { motifImage = trimTransparent(img); render(); });
    if (card.dataset.motifLandscape) loadImage(card.dataset.motifLandscape).then(img => { motifWide = trimTransparent(img); render(); });
    loadPhoto('portrait').then(p => { if (p) render(); });
    loadPhoto('landscape').then(p => { if (p) render(); });
  }

  /* ---------- cart ---------- */
  function initCart(root) {
    root.querySelectorAll('[data-cart-preview]').forEach(canvas => {
      if (ready.has(canvas)) return;
      ready.add(canvas);
      const d = canvas.dataset;
      const style = { family: d.font, weight: d.weight, spacing: Number(d.spacing) || 0, transform: d.transform, scale: Number(d.scale) || 0, stroke: Number(d.stroke) || 0 };
      const cartLayout = parseLayout(d.layout);
      const motifSrc = d.shape === 'landscape' && d.motifLandscape ? d.motifLandscape : d.motif;
      Promise.all([ensureFonts([style, DATE_STYLE].concat(layoutFonts(cartLayout))), loadPhotos()]).then(() => loadImage(motifSrc)).then(trimTransparent).then(img => {
        canvas.hidden = false; // shown first so it can be drawn at its real size
        try {
          draw(canvas, { linen: d.linen, thread: d.thread, style, names: d.names, date: d.date, shape: d.shape, border: d.border === 'true', motifImage: img, placeholder: 'Your names', layout: cartLayout });
        } catch (e) { canvas.hidden = true; return; }
        const cartPhoto = canvas.parentElement?.querySelector('img');
        if (cartPhoto) cartPhoto.hidden = true;
      });
    });
  }

  function init(root) {
    const scope = root || document;
    scope.querySelectorAll('[data-product-section]').forEach(initProduct);
    initCart(scope);
  }

  document.addEventListener('shopify:section:load', event => init(event.target));
  init();
})();
